import { useApolloClient } from '@apollo/client';
import { t } from '@lingui/macro';
import noop from 'lodash/noop';

import Download from '@decisiv/iconix/lib/components/Download';

import Button from '@decisiv/ui-components/lib/components/Button';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import { useNotifications } from '@decisiv/ui-components/lib/components/Notifications';

import { USER_EVENT_KINDS, useUserEventHandler } from 'setup/UserEvents';
import { isImportOpen } from 'utils/bulkImportWizardRegistry';

import { RESOLVERS } from './resolvers';

const TOAST_DURATION = 10000;

/**
 * Surfaces the outcome of a bulk import the user is no longer watching.
 *
 * Renders nothing — it listens app-wide for completion events and raises a
 * toast, so the result still reaches the user after they closed the wizard,
 * navigated away, reloaded, or closed the tab entirely.
 */
export default function BulkImportToasts() {
  const client = useApolloClient();
  const { notify } = useNotifications();

  useUserEventHandler(USER_EVENT_KINDS.BULK_IMPORT_COMPLETED, async (event) => {
    const importId = event.subjectId;
    const importType = (event.payload as { import_type?: string } | null)
      ?.import_type;

    // Nothing identifiable to fetch. Consume it rather than retry forever.
    if (!importId || !importType) return true;

    // The wizard is still open on this import and shows the outcome on its own
    // summary step. Consume it so it can't resurface as a toast later.
    if (isImportOpen(importId)) return true;

    const resolver = RESOLVERS[importType];
    // An import type this build doesn't know about. Leave it unread so a later
    // release can still handle it.
    if (!resolver) return false;

    let outcome;
    try {
      outcome = await resolver.fetch(client, importId);
    } catch {
      // Probably transient — let the next catch-up try again.
      return false;
    }

    if (!outcome) return false;

    const { intent, title, message } = resolver.copy(outcome);
    const { reportUrl } = outcome;

    notify(
      // `id` is not a dedupe key — NotificationsProvider uniquifies it
      // internally — so it's here for traceability. Duplicate delivery is
      // already handled by UserEventsProvider.
      {
        id: `bulk-import-${importType}-${importId}`,
        duration: TOAST_DURATION,
        intent,
        title,
        onClose: noop,
      } as any,
      <Flex
        flexDirection="column"
        alignItems="flex-start"
        style={{ rowGap: '12px' }}
      >
        {message}
        {reportUrl && (
          <Button
            text={t`Download Report`}
            icon={Download}
            size="small"
            kind="secondary"
            onClick={() => window.location.assign(reportUrl)}
          />
        )}
      </Flex>,
    );

    return true;
  });

  return null;
}
